/**
 * Shared "text-only post" gradient card — used on both the public Feed and
 * the Homepage "The Feed" preview so both match visually.
 *
 * Palette matches the homepage theme (purple / fuchsia / indigo / cyan / blue)
 * and is chosen deterministically from the post_id so the same post always
 * shows the same colour.
 */
export const TEXT_POST_PALETTES = [
  'from-[#6B2DFF] via-[#B624E0] to-[#FF2E9A]',   // purple → magenta (signature)
  'from-[#4338CA] via-[#8B5CF6] to-[#D946EF]',   // indigo → violet → fuchsia
  'from-[#1E1B4B] via-[#6D28D9] to-[#EC4899]',   // midnight → purple → pink
  'from-[#0EA5E9] via-[#6366F1] to-[#A855F7]',   // sky → indigo → purple
  'from-[#2563EB] via-[#7C3AED] to-[#EC4899]',   // blue → violet → pink
  'from-[#06B6D4] via-[#3B82F6] to-[#8B5CF6]',   // cyan → blue → violet
];

export function paletteForId(postId) {
  const s = String(postId || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TEXT_POST_PALETTES[h % TEXT_POST_PALETTES.length];
}

export default function TextOnlyPostArt({ postId, caption, aspect = 'square', compact = false }) {
  const palette = paletteForId(postId);
  const text = (caption || '').trim();
  const aspectClass = aspect === 'square'
    ? 'aspect-square sm:aspect-[4/3]'
    : aspect === 'tall'
      ? 'aspect-[4/5]'
      : 'aspect-square';

  // Scale text size by caption length. Mobile sizes are deliberately small
  // so brand words like "EntrepreNatives" never mid-word-wrap.
  const sizeClass = compact
    ? (text.length <= 40 ? 'text-lg sm:text-xl' : text.length <= 120 ? 'text-sm sm:text-base' : 'text-xs sm:text-sm')
    : (text.length <= 40
        ? 'text-2xl sm:text-4xl md:text-5xl'
        : text.length <= 120
          ? 'text-xl sm:text-3xl md:text-4xl'
          : text.length <= 240
            ? 'text-lg sm:text-2xl md:text-3xl'
            : 'text-base sm:text-lg md:text-xl');

  return (
    <div
      className={`relative w-full ${aspectClass} bg-gradient-to-br ${palette} overflow-hidden flex items-center justify-center ${compact ? 'p-4' : 'px-4 sm:px-8 py-10'}`}
      data-testid={`post-text-art-${postId}`}
    >
      {/* Soft top-left highlight (no dark vignette → no "box" feel) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 55%)' }}
      />
      <p
        className={`relative mx-auto text-white font-syne font-extrabold text-center leading-tight tracking-tight ${sizeClass}`}
        style={{
          wordBreak: 'keep-all',
          overflowWrap: 'normal',
          textWrap: 'balance',
          whiteSpace: 'pre-wrap',
          textAlign: 'center',
          width: '100%',
        }}
      >
        {text}
      </p>
    </div>
  );
}
